// frontend/src/components/GiftManagement.js
import React, { useState, useEffect, useContext, useCallback } from 'react'; // Добавлен useCallback
import { SettingsContext } from '../context/SettingsContext'; // Импортируем SettingsContext
import { FaToggleOn, FaToggleOff, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa'; // Импортируем иконки
import './GiftManagement.css';

const GiftManagement = () => {
  const { settings: globalSettings, refreshSettings } = useContext(SettingsContext); // Используем контекст настроек
  const [isGiftSectionActive, setIsGiftSectionActive] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [allContent, setAllContent] = useState([]); // Весь контент для выбора
  const [giftCovers, setGiftCovers] = useState([]); // Загруженные обложки
  const [purchasedGifts, setPurchasedGifts] = useState([]); // Купленные подарки
  
  const [loading, setLoading] = useState({
    content: true,
    covers: true, // Пока оставим true, реализуем позже
    gifts: true,  // Пока оставим true, реализуем позже
  });
  const [error, setError] = useState({
    content: null,
    covers: null,
    gifts: null,
  });

  const fetchAllAdminContent = useCallback(async () => {
    setLoading(prev => ({ ...prev, content: true }));
    setError(prev => ({ ...prev, content: null }));
    try {
      const telegramId = localStorage.getItem('telegram_id');
      // Запрашиваем все посты, включая неопубликованные, и большое количество (limit)
      const response = await fetch('/api/content?published=all&limit=1000&sortBy=sortOrder', {
        headers: { 'X-Telegram-ID': telegramId }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка загрузки контента' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      setAllContent(data.content || []);
    } catch (err) {
      console.error('Ошибка при загрузке всего контента для админки:', err);
      setError(prev => ({ ...prev, content: err.message }));
      setAllContent([]);
    } finally {
      setLoading(prev => ({ ...prev, content: false }));
    }
  }, []);

  const fetchGiftCovers = useCallback(async () => {
    setLoading(prev => ({ ...prev, covers: true }));
    setError(prev => ({ ...prev, covers: null }));
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch('/api/gifts/admin/covers', {
        headers: { 'X-Telegram-ID': telegramId }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка загрузки обложек' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      setGiftCovers(data || []);
    } catch (err) {
      console.error('Ошибка при загрузке обложек для подарков:', err);
      setError(prev => ({ ...prev, covers: err.message }));
      setGiftCovers([]);
    } finally {
      setLoading(prev => ({ ...prev, covers: false }));
    }
  }, []);

  const fetchPurchasedGifts = useCallback(async () => {
    setLoading(prev => ({ ...prev, gifts: true }));
    setError(prev => ({ ...prev, gifts: null }));
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch('/api/gifts/admin', {
        headers: { 'X-Telegram-ID': telegramId }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка загрузки списка подарков' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const data = await response.json();
      setPurchasedGifts(data || []);
    } catch (err) {
      console.error('Ошибка при загрузке купленных подарков:', err);
      setError(prev => ({ ...prev, gifts: err.message }));
      setPurchasedGifts([]);
    } finally {
      setLoading(prev => ({ ...prev, gifts: false }));
    }
  }, []);
  
  // useEffect для загрузки всех данных при монтировании
  useEffect(() => {
    fetchAllAdminContent();
    fetchGiftCovers();
    fetchPurchasedGifts();
  }, [fetchAllAdminContent, fetchGiftCovers, fetchPurchasedGifts]);

  // Единый useEffect для синхронизации isGiftSectionActive с globalSettings
  useEffect(() => {
    if (globalSettings) {
      if (globalSettings.gifts_section_active !== undefined) {
        setIsGiftSectionActive(globalSettings.gifts_section_active);
      } else {
        // Если gifts_section_active отсутствует в настройках, считаем его false
        setIsGiftSectionActive(false);
      }
    }
    // Если globalSettings еще не загружен, isGiftSectionActive останется своим начальным значением (false)
  }, [globalSettings]);


  const toggleGiftSectionStatus = async () => {
    const newStatus = !isGiftSectionActive;
    const telegramId = localStorage.getItem('telegram_id');
    try {
      const response = await fetch('/api/settings/gifts_section_active', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId,
        },
        body: JSON.stringify({
          value: newStatus,
          description: 'Статус активности раздела Подарки (управляется из GiftManagement)',
          group: 'gifts', // Можно выделить в отдельную группу
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Ошибка обновления статуса раздела подарков' }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      const updatedSetting = await response.json();
      setIsGiftSectionActive(updatedSetting.value);
      // Обновляем глобальные настройки, чтобы HomePage и другие компоненты это увидели
      await refreshSettings(); 
      alert(`Раздел подарков ${updatedSetting.value ? 'включен' : 'выключен'}`);
    } catch (err) {
      console.error('Ошибка при изменении статуса раздела подарков:', err);
      alert(`Ошибка: ${err.message}`);
    }
  };

  const toggleAccordion = (accordionName) => {
    setActiveAccordion(activeAccordion === accordionName ? null : accordionName);
  };

  const handleGiftableChange = async (contentId) => {
    const telegramId = localStorage.getItem('telegram_id');
    try {
      const response = await fetch(`/api/content/${contentId}/toggle-giftable`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId 
        }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка изменения статуса "доступен для подарка"' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const updatedContentData = await response.json();
      console.log('[GiftManagement] Response from toggle-giftable:', updatedContentData);
      // Обновляем состояние allContent, чтобы отразить изменения
      const newAllContent = prevAll => 
        prevAll.map(c => c._id === contentId ? updatedContentData.content : c);
      setAllContent(newAllContent);
      console.log('[GiftManagement] Updated allContent locally with (isGiftable):', updatedContentData.content?.isGiftable);
      alert(updatedContentData.message);
    } catch (err) {
      console.error(`Ошибка при изменении isGiftable для ${contentId}:`, err);
      alert(`Ошибка: ${err.message}`);
      // Можно откатить изменение в UI, если нужно, или перезапросить данные
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('coverImage', file);
    // Можно добавить поле name, если нужно передавать его отдельно
    // formData.append('name', file.name); 

    setLoading(prev => ({ ...prev, covers: true })); // Показываем индикатор загрузки для секции обложек
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch('/api/gifts/admin/covers', {
        method: 'POST',
        headers: { 
          // 'Content-Type': 'multipart/form-data' // Multer устанавливает это автоматически
          'X-Telegram-ID': telegramId 
        },
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка загрузки обложки' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const newCoverData = await response.json();
      setGiftCovers(prev => [...prev, newCoverData.cover]);
      alert(newCoverData.message);
    } catch (err) {
      console.error('Ошибка при загрузке обложки:', err);
      alert(`Ошибка загрузки: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, covers: false }));
      event.target.value = null; // Сбрасываем input file
    }
  };

  const handleDeleteCover = async (coverId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту обложку?')) return;

    setLoading(prev => ({ ...prev, covers: true }));
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch(`/api/gifts/admin/covers/${coverId}`, {
        method: 'DELETE',
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка удаления обложки' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const result = await response.json();
      setGiftCovers(prev => prev.filter(cover => cover._id !== coverId));
      alert(result.message);
    } catch (err) {
      console.error('Ошибка при удалении обложки:', err);
      alert(`Ошибка удаления: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, covers: false }));
    }
  };
  
  const handleCancelGiftActivation = async (giftId) => {
    if (!window.confirm('Вы уверены, что хотите отменить активацию этого подарка? Он снова станет доступен для активации по той же ссылке.')) return;
    
    // setLoading(prev => ({ ...prev, gifts: true })); // Убираем, чтобы список не исчезал
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch(`/api/gifts/admin/${giftId}/cancel`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'X-Telegram-ID': telegramId 
        },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка отмены активации' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const updatedGiftData = await response.json();
      setPurchasedGifts(prev => prev.map(g => g._id === giftId ? updatedGiftData.gift : g));
      alert(updatedGiftData.message);
    } catch (err) {
      console.error('Ошибка при отмене активации подарка:', err);
      alert(`Ошибка: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, gifts: false }));
    }
  };

  const handleDeleteGift = async (giftId) => {
    const giftToDelete = purchasedGifts.find(g => g._id === giftId);
    if (giftToDelete && giftToDelete.isActivated) {
        alert('Нельзя удалить активированный подарок. Сначала отмените его активацию.');
        return;
    }
    if (!window.confirm('Вы уверены, что хотите удалить этот подарок? Это действие необратимо.')) return;

    // setLoading(prev => ({ ...prev, gifts: true })); // Убираем, чтобы список не исчезал
    try {
      const telegramId = localStorage.getItem('telegram_id');
      const response = await fetch(`/api/gifts/admin/${giftId}`, {
        method: 'DELETE',
        headers: { 'X-Telegram-ID': telegramId },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: 'Ошибка удаления подарка' }));
        throw new Error(errData.message || `HTTP error ${response.status}`);
      }
      const result = await response.json();
      setPurchasedGifts(prev => prev.filter(g => g._id !== giftId));
      alert(result.message);
    } catch (err) {
      console.error('Ошибка при удалении подарка:', err);
      alert(`Ошибка удаления: ${err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, gifts: false }));
    }
  };

  return (
    <div className="gift-management">
      <h2>Управление Подарками</h2>

      <button
        onClick={toggleGiftSectionStatus}
        className={`gift-toggle-button ${isGiftSectionActive ? 'active' : ''}`}
      >
        {isGiftSectionActive ? 
          <FaToggleOn className="gift-toggle-icon on" /> : 
          <FaToggleOff className="gift-toggle-icon off" />
        }
        Раздел Подарков: {isGiftSectionActive ? 'Включен' : 'Выключен'}
      </button>

      {/* Аккордеон: Настройка материалов для подарков */}
      <div className="accordion-item">
        <button 
          className={`accordion-header ${activeAccordion === 'giftableItems' ? 'open' : ''}`} 
          onClick={() => toggleAccordion('giftableItems')}
        >
          Материалы, доступные для подарка
          <span className="accordion-icon">▼</span>
        </button>
        <div className={`accordion-content ${activeAccordion === 'giftableItems' ? 'open' : ''}`}>
          {loading.content ? <p>Загрузка материалов...</p> : (
            <ul>
              {allContent.filter(c => !c.isFree).map(contentItem => (
                <li key={contentItem._id}>
                  <label>
                    <input 
                      type="checkbox"
                      checked={contentItem.isGiftable || false} 
                      onChange={() => handleGiftableChange(contentItem._id)}
                      disabled={contentItem.isFree && !contentItem.isGiftable} 
                    />
                    {contentItem.title} ({contentItem.price} руб.)
                    {contentItem.isFree && <span style={{color: 'var(--hint-color)', marginLeft: '5px'}}>(бесплатный, нельзя дарить)</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <p className="gift-note">По умолчанию все новые платные материалы доступны для подарка. Здесь вы можете исключить их.</p>
        </div>
      </div>

      {/* Аккордеон: Обложки для подарков */}
      <div className="accordion-item">
        <button 
          className={`accordion-header ${activeAccordion === 'giftCovers' ? 'open' : ''}`} 
          onClick={() => toggleAccordion('giftCovers')}
        >
          Обложки для подарков
          <span className="accordion-icon">▼</span>
        </button>
        <div className={`accordion-content ${activeAccordion === 'giftCovers' ? 'open' : ''}`}>
          {loading.covers ? <p>Загрузка обложек...</p> : error.covers ? <p className="error-message">Ошибка: {error.covers}</p> : (
            <div className="gift-covers-admin-list">
              {giftCovers.length === 0 && <p>Нет загруженных обложек.</p>}
              {giftCovers.map(cover => (
                <div key={cover._id} className="gift-cover-admin-item">
                  <img src={cover.url} alt={cover.name || 'Обложка подарка'} />
                  {/* <span>{cover.name}</span> Убираем отображение имени файла */}
                  <button 
                    onClick={() => handleDeleteCover(cover._id)} 
                    className="admin-button admin-button-delete" // Используем общие стили кнопок
                    style={{ fontSize: '0.8em', padding: '4px 8px', width: '100%', marginTop: '5px' }} // Компактный размер
                  >
                    Удалить
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="gift-cover-upload" style={{ marginTop: '15px' }}>
            <label htmlFor="cover-upload-input" className="admin-button"> {/* Используем общий стиль кнопки */}
              Загрузить новую обложку
            </label>
            <input id="cover-upload-input" type="file" accept="image/*" onChange={handleCoverUpload} style={{display: 'none'}}/>
          </div>
        </div>
      </div>

      {/* Список купленных подарков */}
      <h3>Учет Подарков</h3>
      {loading.gifts ? <p>Загрузка списка подарков...</p> : error.gifts ? <p className="error-message">Ошибка: {error.gifts}</p> : (
        <div className="purchased-gifts-list">
          {purchasedGifts.length > 0 ? purchasedGifts.map(gift => (
            <div key={gift._id} className="gift-item-card">
              <div className="gift-item-card-header">
                <div className="gift-item-title">
                  <strong>Подарок:</strong> {gift.content?.title || gift.content?._id || 'N/A'}
                </div>
                <div className={`gift-item-status ${gift.isActivated ? 'activated' : 'not-activated'}`} title={gift.isActivated ? 'Активирован' : 'Не активирован'}>
                  {gift.isActivated ? <FaCheckCircle /> : <FaHourglassHalf />}
                </div>
              </div>
              <div className="gift-item-details">
                <p><strong>ID:</strong> {gift._id.slice(-6)}</p>
                <p>
                  <strong>Купил:</strong> 
                  {gift.buyer?.username ? (
                    <a href={`https://t.me/${gift.buyer.username}`} target="_blank" rel="noopener noreferrer">
                      @{gift.buyer.username}
                    </a>
                  ) : (
                    gift.buyer?.telegramId || 'N/A'
                  )}
                </p>
                <p>
                  <strong>Активировал:</strong> 
                  {gift.activatedBy?.username ? (
                    <a href={`https://t.me/${gift.activatedBy.username}`} target="_blank" rel="noopener noreferrer">
                      @{gift.activatedBy.username}
                    </a>
                  ) : (
                    gift.activatedBy?.telegramId || '-'
                  )}
                </p>
                <p><strong>Дата покупки:</strong> {new Date(gift.createdAt).toLocaleString('ru-RU')}</p>
                {gift.isActivated && gift.activatedAt && (
                  <p><strong>Дата активации:</strong> {new Date(gift.activatedAt).toLocaleString('ru-RU')}</p>
                )}
              </div>
              <div className="gift-item-actions">
                {!gift.isActivated && (
                  <button
                    onClick={() => {
                      const giftLink = `${window.location.origin}/gift/${gift.uniqueGiftId}`; // Предполагаем, что ссылка формируется так
                      navigator.clipboard.writeText(giftLink)
                        .then(() => alert('Ссылка на подарок скопирована!'))
                        .catch(err => alert('Не удалось скопировать ссылку: ' + err));
                    }}
                    className="admin-button"
                  >
                    Копировать ссылку
                  </button>
                )}
                {gift.isActivated && (
                  <button
                    onClick={() => handleCancelGiftActivation(gift._id)} 
                    className="admin-button"
                    style={{ backgroundColor: 'var(--warning-color)', color: '#000' }}
                  >
                    Отменить
                  </button>
                )}
                <button 
                  onClick={() => handleDeleteGift(gift._id)} 
                  className="admin-button admin-button-delete"
                >
                  Удалить
                </button>
              </div>
            </div>
          )) : (
            <p style={{ textAlign: 'center', color: 'var(--hint-color)' }}>Пока нет купленных подарков.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftManagement;
